import { type FrameGraphTask, type IDisposable, type Nullable } from "core/index";
import { type ServiceDefinition } from "shared-ui-components/modularTool/modularity/serviceDefinition";
import { type IGizmoService, GizmoServiceIdentity } from "../../gizmoService";
import { type ISceneContext, SceneContextIdentity } from "../../sceneContext";
import { type IWatcherService, WatcherServiceIdentity } from "../../watcherService";
import { type ISceneExplorerService, SceneExplorerServiceIdentity } from "./sceneExplorerService";

import { tokens } from "@fluentui/react-components";
import {
    BorderNoneRegular,
    BorderOutsideRegular,
    BubbleMultipleRegular,
    CameraRegular,
    EditRegular,
    EyeOffRegular,
    EyeRegular,
    FlashlightOffRegular,
    FlashlightRegular,
    LightbulbRegular,
    MyLocationRegular,
    VideoFilled,
    VideoRegular,
} from "@fluentui/react-icons";

import { Camera } from "core/Cameras/camera";
import { FindMainCamera } from "core/FrameGraph/frameGraphUtils";
import { ClusteredLightContainer } from "core/Lights/Clustered/clusteredLightContainer";
import { Light } from "core/Lights/light";
import { AbstractMesh } from "core/Meshes/abstractMesh";
import { Mesh } from "core/Meshes/mesh";
import { TransformNode } from "core/Meshes/transformNode";
import { Observable } from "core/Misc/observable";
import { Node } from "core/node";
import { MeshIcon } from "shared-ui-components/fluent/icons";
import { EditNodeGeometry, GetNodeGeometry } from "../../../misc/nodeGeometryEditor";
import { RecordEntityPropertyOverride } from "../../overrideCapture";
import { DefaultCommandsOrder, DefaultSectionsOrder } from "./defaultSectionsMetadata";

import "core/Rendering/boundingBoxRenderer";

function IsCameraFrameGraphTask(task: FrameGraphTask): task is FrameGraphTask & { camera: Camera } {
    return (task as Partial<{ camera: Camera }>).camera instanceof Camera;
}

function IsNodesSectionType(node: Node): boolean {
    return node instanceof TransformNode || node instanceof Camera || node instanceof Light;
}

export const NodeExplorerServiceDefinition: ServiceDefinition<[], [ISceneExplorerService, ISceneContext, IGizmoService, IWatcherService]> = {
    friendlyName: "Node Explorer",
    consumes: [SceneExplorerServiceIdentity, SceneContextIdentity, GizmoServiceIdentity, WatcherServiceIdentity],
    factory: (sceneExplorerService, sceneContext, gizmoService, watcherService) => {
        const scene = sceneContext.currentScene;
        if (!scene) {
            return undefined;
        }

        const nodeMovedObservable = new Observable<Node>();

        // Set of all nodes known to be in the scene, rebuilt each time getRootEntities
        // is called. Used by getEntityDisplayInfo to detect orphaned ancestor nodes.
        const knownSceneNodes = new Set<Node>();

        const sectionRegistration = sceneExplorerService.addSection({
            displayName: "Nodes",
            order: DefaultSectionsOrder.Nodes,
            getRootEntities: () => {
                const rootNodes = new Set<Node>(scene.rootNodes);
                knownSceneNodes.clear();

                // Ensure all nodes in the scene are reachable in the explorer, even if their
                // parent was removed from the scene or is not a type shown in the Nodes section.
                for (const node of [...scene.meshes, ...scene.transformNodes, ...scene.cameras, ...scene.lights]) {
                    knownSceneNodes.add(node);

                    if (!node.parent) {
                        continue;
                    }

                    if (!IsNodesSectionType(node.parent)) {
                        // Parent is not a type shown in the Nodes section (e.g. a Bone).
                        // Treat this node as a root so it still appears in the explorer.
                        rootNodes.add(node);
                    } else {
                        // Walk up through Nodes-section-type parents to find the topmost ancestor.
                        // If that ancestor was removed from the scene (not in rootNodes), add it
                        // so the entire subtree remains visible in the explorer.
                        let ancestor: Node = node.parent;
                        while (ancestor.parent && IsNodesSectionType(ancestor.parent)) {
                            ancestor = ancestor.parent;
                        }
                        rootNodes.add(ancestor);
                    }
                }

                // Lights within a clustered light container are not included in Scene.lights or Scene.rootNodes.
                // If they also have no parent, then they won't show up anywhere, so show them as root nodes.
                for (const light of scene.lights) {
                    if (light instanceof ClusteredLightContainer) {
                        for (const childLight of light.lights) {
                            knownSceneNodes.add(childLight);
                            if (!childLight.parent) {
                                rootNodes.add(childLight);
                            }
                        }
                    }
                }

                return [...rootNodes];
            },
            getEntityChildren: (node) => node.getChildren(),
            getEntityDisplayInfo: (node) => {
                const onChangeObservable = new Observable<void>();

                const nameHookToken = watcherService.watchProperty(node, "name", () => onChangeObservable.notifyObservers());

                const parentHookToken = watcherService.watchProperty(node, "parent", () => nodeMovedObservable.notifyObservers(node));

                // A node is "not in the scene" if it is a Nodes-section type but is not
                // a known scene node. This handles nodes that were removed from the scene
                // but still appear because a descendant is in the scene. Nodes from the
                // !IsNodesSectionType(parent) branch are unaffected because they always
                // come from the scene's tracking lists. Clustered light children are also
                // unaffected because they are added to knownSceneNodes explicitly.
                const validationError =
                    IsNodesSectionType(node) && !knownSceneNodes.has(node) ? "This entity is not in the scene but is shown because a descendant is still in the scene." : undefined;

                return {
                    get name() {
                        return node.name || `Unnamed ${node.getClassName()}`;
                    },
                    validationError,
                    onChange: onChangeObservable,
                    dispose: () => {
                        nameHookToken.dispose();
                        parentHookToken.dispose();
                        onChangeObservable.clear();
                    },
                };
            },
            entityIcon: ({ entity: node }) =>
                node instanceof AbstractMesh ? (
                    <MeshIcon color={tokens.colorPaletteBlueForeground2} />
                ) : node instanceof TransformNode ? (
                    <MyLocationRegular color={tokens.colorPaletteBlueForeground2} />
                ) : node instanceof Camera ? (
                    <CameraRegular color={tokens.colorPaletteGreenForeground2} />
                ) : node instanceof ClusteredLightContainer ? (
                    <BubbleMultipleRegular color={tokens.colorPaletteYellowForeground2} />
                ) : node instanceof Light ? (
                    <LightbulbRegular color={tokens.colorPaletteYellowForeground2} />
                ) : (
                    <></>
                ),
            getEntityAddedObservables: () => [
                scene.onNewMeshAddedObservable,
                scene.onNewTransformNodeAddedObservable,
                scene.onNewCameraAddedObservable,
                scene.onNewLightAddedObservable,
            ],
            getEntityRemovedObservables: () => [
                scene.onMeshRemovedObservable,
                scene.onTransformNodeRemovedObservable,
                scene.onCameraRemovedObservable,
                scene.onLightRemovedObservable,
            ],
            getEntityMovedObservables: () => [nodeMovedObservable],
            dragDropConfig: {
                canDrag: (node) => node instanceof Node,
                canDrop: (draggedNode, targetNode) => {
                    // Can't drop on self
                    if (targetNode === draggedNode) {
                        return false;
                    }
                    // Can't drop on a descendant
                    if (targetNode !== null && targetNode.isDescendantOf(draggedNode)) {
                        return false;
                    }
                    // Can drop onto section root (null) only if node has a parent
                    if (targetNode === null) {
                        return draggedNode.parent !== null;
                    }
                    return true;
                },
                onDrop: (draggedNode, targetNode) => {
                    if (draggedNode.parent === targetNode) {
                        return;
                    }
                    const nodeScene = draggedNode.getScene();
                    const oldParent = draggedNode.parent;
                    // Use setParent for TransformNodes to preserve world transform
                    if (draggedNode instanceof TransformNode) {
                        // setParent recomputes local position/rotation/scaling to
                        // preserve the world transform, so capture those alongside
                        // the parent change to round-trip the reparent on reload.
                        const oldPosition = draggedNode.position.clone();
                        const oldRotation = draggedNode.rotation.clone();
                        const oldRotationQuaternion = draggedNode.rotationQuaternion ? draggedNode.rotationQuaternion.clone() : null;
                        const oldScaling = draggedNode.scaling.clone();

                        draggedNode.setParent(targetNode);

                        RecordEntityPropertyOverride(nodeScene, draggedNode, "parent", targetNode, oldParent);
                        RecordEntityPropertyOverride(nodeScene, draggedNode, "position", draggedNode.position, oldPosition);
                        if (draggedNode.rotationQuaternion) {
                            RecordEntityPropertyOverride(nodeScene, draggedNode, "rotationQuaternion", draggedNode.rotationQuaternion, oldRotationQuaternion);
                        } else {
                            RecordEntityPropertyOverride(nodeScene, draggedNode, "rotation", draggedNode.rotation, oldRotation);
                        }
                        RecordEntityPropertyOverride(nodeScene, draggedNode, "scaling", draggedNode.scaling, oldScaling);
                    } else {
                        draggedNode.parent = targetNode;
                        RecordEntityPropertyOverride(nodeScene, draggedNode, "parent", targetNode, oldParent);
                    }
                },
            },
        });

        const abstractMeshBoundingBoxCommandRegistration = sceneExplorerService.addEntityCommand({
            predicate: (entity: unknown): entity is AbstractMesh => entity instanceof AbstractMesh && entity.getTotalVertices() > 0,
            order: DefaultCommandsOrder.MeshBoundingBox,
            getCommand: (mesh) => {
                const onChangeObservable = new Observable<void>();
                const showBoundingBoxHook = watcherService.watchProperty(mesh, "showBoundingBox", () => onChangeObservable.notifyObservers());

                return {
                    type: "toggle",
                    get displayName() {
                        return `${mesh.showBoundingBox ? "Hide" : "Show"} Bounding Box`;
                    },
                    icon: () => (mesh.showBoundingBox ? <BorderOutsideRegular /> : <BorderNoneRegular />),
                    get isEnabled() {
                        return mesh.showBoundingBox;
                    },
                    set isEnabled(enabled: boolean) {
                        mesh.showBoundingBox = enabled;
                    },
                    onChange: onChangeObservable,
                    dispose: () => {
                        showBoundingBoxHook.dispose();
                        onChangeObservable.clear();
                    },
                };
            },
        });

        const abstractMeshVisibilityCommandRegistration = sceneExplorerService.addEntityCommand({
            predicate: (entity: unknown): entity is AbstractMesh => entity instanceof AbstractMesh && entity.getTotalVertices() > 0,
            order: DefaultCommandsOrder.MeshVisibility,
            getCommand: (mesh) => {
                const onChangeObservable = new Observable<void>();
                const isVisibleHook = watcherService.watchProperty(mesh, "isVisible", () => onChangeObservable.notifyObservers());

                return {
                    type: "toggle",
                    get displayName() {
                        return `${mesh.isVisible ? "Hide" : "Show"} Mesh`;
                    },
                    icon: () => (mesh.isVisible ? <EyeRegular /> : <EyeOffRegular />),
                    hotKey: {
                        keyCode: "Space",
                        control: true,
                    },
                    get isEnabled() {
                        return !mesh.isVisible;
                    },
                    set isEnabled(enabled: boolean) {
                        const oldValue = mesh.isVisible;
                        mesh.isVisible = !enabled;
                        RecordEntityPropertyOverride(mesh.getScene(), mesh, "isVisible", mesh.isVisible, oldValue);
                    },
                    onChange: onChangeObservable,
                    dispose: () => {
                        isVisibleHook.dispose();
                        onChangeObservable.clear();
                    },
                };
            },
        });

        const getActiveCamera = () => (scene.frameGraph ? FindMainCamera(scene.frameGraph) : scene.activeCamera);

        const activeCameraCommandRegistration = sceneExplorerService.addEntityCommand({
            predicate: (entity: unknown) => entity instanceof Camera,
            order: DefaultCommandsOrder.CameraActive,
            getCommand: (camera) => {
                const scene = camera.getScene();
                const onChangeObservable = new Observable<void>();
                const activeCameraChangedObserver = scene.onActiveCameraChanged.add(() => {
                    onChangeObservable.notifyObservers();
                });

                return {
                    type: "toggle",
                    displayName: "Activate and Attach Controls",
                    icon: () => {
                        return getActiveCamera() === camera ? <VideoFilled /> : <VideoRegular />;
                    },
                    get isEnabled() {
                        return getActiveCamera() === camera;
                    },
                    set isEnabled(enabled: boolean) {
                        const activeCamera = getActiveCamera();
                        if (enabled && activeCamera !== camera) {
                            if (scene.frameGraph) {
                                void (async (frameGraph) => {
                                    let updated = false;
                                    const nrg = frameGraph.getLinkedNodeRenderGraph();
                                    if (nrg) {
                                        updated = await nrg.replaceCameraAsync(activeCamera, camera);
                                    } else {
                                        for (const task of frameGraph.tasks) {
                                            if (IsCameraFrameGraphTask(task)) {
                                                task.camera = camera;
                                                updated = true;
                                            }
                                        }
                                    }
                                    if (updated) {
                                        activeCamera?.detachControl();
                                        camera.attachControl(true);
                                        onChangeObservable.notifyObservers();
                                    }
                                })(scene.frameGraph);
                            } else {
                                activeCamera?.detachControl();
                                scene.activeCamera = camera;
                                camera.attachControl(true);
                            }
                        }
                    },
                    onChange: onChangeObservable,
                    dispose: () => {
                        activeCameraChangedObserver.remove();
                        onChangeObservable.clear();
                    },
                };
            },
        });

        function addGizmoCommand<NodeT extends Node>(nodeClass: abstract new (...args: any[]) => NodeT, getGizmoRef: (node: NodeT) => IDisposable) {
            return sceneExplorerService.addEntityCommand({
                predicate: (entity: unknown): entity is NodeT => entity instanceof nodeClass,
                order: DefaultCommandsOrder.GizmoActive,
                getCommand: (node) => {
                    const onChangeObservable = new Observable<void>();

                    let gizmoRef: Nullable<IDisposable> = null;

                    return {
                        type: "toggle",
                        get displayName() {
                            return `Turn ${gizmoRef ? "Off" : "On"} Gizmo`;
                        },
                        icon: () => (gizmoRef ? <EyeRegular /> : <EyeOffRegular />),
                        get isEnabled() {
                            return !!gizmoRef;
                        },
                        set isEnabled(enabled: boolean) {
                            if (enabled) {
                                if (!gizmoRef) {
                                    gizmoRef = getGizmoRef(node);
                                    onChangeObservable.notifyObservers();
                                }
                            } else {
                                if (gizmoRef) {
                                    gizmoRef.dispose();
                                    gizmoRef = null;
                                    onChangeObservable.notifyObservers();
                                }
                            }
                        },
                        onChange: onChangeObservable,
                        dispose: () => {
                            onChangeObservable.clear();
                        },
                    };
                },
            });
        }

        const cameraGizmoCommandRegistration = addGizmoCommand(Camera, gizmoService.getCameraGizmo.bind(gizmoService));

        const lightEnabledCommandRegistration = sceneExplorerService.addEntityCommand({
            predicate: (entity: unknown): entity is Light => entity instanceof Light,
            order: DefaultCommandsOrder.LightActive,
            getCommand: (light) => {
                return {
                    type: "toggle",
                    get displayName() {
                        return `Turn Light ${light.isEnabled() ? "Off" : "On"}`;
                    },
                    icon: () => (light.isEnabled() ? <FlashlightRegular /> : <FlashlightOffRegular />),
                    get isEnabled() {
                        return !light.isEnabled();
                    },
                    set isEnabled(enabled: boolean) {
                        light.setEnabled(!enabled);
                    },
                    onChange: light.onEnabledStateChangedObservable,
                };
            },
        });

        const lightGizmoCommandRegistration = addGizmoCommand(Light, gizmoService.getLightGizmo.bind(gizmoService));

        const editNodeGeometryCommandRegistration = sceneExplorerService.addEntityCommand({
            predicate: (entity: unknown): entity is Mesh => entity instanceof Mesh && !!GetNodeGeometry(entity),
            order: DefaultCommandsOrder.EditNodeGeometry,
            getCommand: (mesh) => {
                return {
                    type: "action",
                    displayName: "Edit in Node Geometry Editor",
                    icon: () => <EditRegular />,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    execute: async () => {
                        const nodeGeometry = GetNodeGeometry(mesh);
                        if (nodeGeometry) {
                            await EditNodeGeometry(nodeGeometry, mesh.getScene());
                        }
                    },
                };
            },
        });

        return {
            dispose: () => {
                sectionRegistration.dispose();
                abstractMeshBoundingBoxCommandRegistration.dispose();
                abstractMeshVisibilityCommandRegistration.dispose();
                activeCameraCommandRegistration.dispose();
                cameraGizmoCommandRegistration.dispose();
                lightEnabledCommandRegistration.dispose();
                lightGizmoCommandRegistration.dispose();
                editNodeGeometryCommandRegistration.dispose();
            },
        };
    },
};
